import type { MaybeRef, Ref } from 'vue'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { getError } from '@autobid/strapi-integration/utils/getError'
import { useCarPageType } from '@autobid/ui/composables/car/useCarPageInfo'
import { useAuction } from '@autobid/ui/composables/useAuction'
import {
  getProfileQueryKey,
  useProfile,
} from '@autobid/ui/composables/useProfile'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, onServerPrefetch, unref } from 'vue'

interface Options {
  /**
   * Provide the carId if you want to accept special conditions for a specific car. You do not have to provide it if you're not going to use
   * a "handleAccept" function
   */
  carId?: MaybeRef<number>
  auctionId: number
  successCallback?: () => void
}

export function useSpecialConditionNotification({
  carId,
  auctionId,
  successCallback,
}: Options) {
  const informedForIds = useCookie<number[]>('specialConditionNotification', {
    maxAge: 60 * 60 * 30,
  })
  const showSpecialConditionNotificationDialog = useState(
    'specialConfitionNotificationPopup',
    () => false,
  ) as Ref<boolean>
  const queryClient = useQueryClient()
  const { isAuthed, sessionData } = useAutobidAuth()
  const { $trpc } = useNuxtApp()
  const push = usePush()
  const { t } = useI18n()
  const {
    data: profile,
    revalidate: revalidateProfile,
    suspense: suspenseProfile,
  } = useProfile()
  const pageType = useCarPageType()
  const { auction } = useAuction(auctionId, {
    enabled: computed(() => pageType.value !== 'carSearch'),
  })
  const doesAuctionMeetConditions = computed(() => {
    if (!auction.value)
      return false

    if (auction.value.stage === 'FINISHED')
      return false

    return !!auction.value.activeStatusesNames?.includes(
      'special_conditions_notification',
    )
  })

  const specialConditionAccepted = computed(() => {
    if (!profile.value?.acceptedSpecialConditionsForAuctions)
      return false

    return profile.value.acceptedSpecialConditionsForAuctions.includes(
      auctionId,
    )
  })

  const shouldDisplayDialog = computed(() => {
    if (!isAuthed.value)
      return false

    if (informedForIds.value && informedForIds.value?.includes(auctionId)) {
      return false
    }

    if (!doesAuctionMeetConditions.value)
      return false

    if (specialConditionAccepted.value) {
      return false
    }

    return true
  })

  const markAsInformed = () => {
    if (informedForIds.value && informedForIds.value?.includes(auctionId))
      return

    informedForIds.value = [...(informedForIds.value || []), auctionId]
  }

  const mutationFn = () => {
    return $trpc.webapi.acceptAuctionSpecialConditions.mutate({
      ...(unref(carId) ? { type: 'car', carId: unref(carId)! } : { type: 'auction', auctionId }),
    })
  }

  const markAuctionIdAsAccepted = () => {
    queryClient.setQueryData(
      getProfileQueryKey({ isAuthed: true, userId: Number(sessionData.value?.user?.id) }),
      () => {
        return {
          ...profile.value,
          acceptedSpecialConditionsForAuctions: [
            ...(profile.value?.acceptedSpecialConditionsForAuctions || []),
            auctionId,
          ],
        }
      },
    )
  }

  const { mutate: handleAccept, isLoading } = useMutation({
    mutationFn,
    onError: (error) => {
      const { message, handled } = getError(error)

      if (!handled) {
        push.error(message)
      }
    },
    onSuccess: () => {
      markAuctionIdAsAccepted()
      revalidateProfile()
      push.success(t('auction-special-condition.accept-success-message'))

      if (successCallback)
        successCallback()
    },
  })

  onServerPrefetch(suspenseProfile)

  return {
    doesAuctionMeetConditions,
    showSpecialConditionNotificationDialog,
    specialConditionAccepted,
    shouldDisplayDialog,
    isLoading,
    handleAccept,
    markAsInformed,
  }
}
